import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Buildings, Sparkle, ArrowRight, CheckCircle, X } from '@phosphor-icons/react';

const AcceptTeamInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [emailExists, setEmailExists] = useState(null); // null = unknown, true/false after check

  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('token') ? localStorage.getItem('user') || 'null' : 'null'); } catch { return null; } })();

  useEffect(() => {
    api.get(`/team/invite/${token}/preview`)
      .then(r => {
        setPreview(r.data);
        // Auto-detect whether the invited email already has a Founderboard account so we
        // can show a single, confidence-inducing CTA instead of an ambiguous Log-in/Sign-up choice.
        return api.get(`/auth/check-email/${encodeURIComponent(r.data.invited_email)}`);
      })
      .then(r => { if (r) setEmailExists(!!r.data.exists); })
      .catch(err => setError(err.response?.data?.detail || 'Invitation not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await api.post(`/team/invite/${token}/accept`, {});
      // If backend reissued a token (role-correction), use it
      if (res.data.token) localStorage.setItem('token', res.data.token);
      // Inherit onboarding from the joined workspace so App.js routes us into the app, not /signup/*
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      u.onboarding_complete = true;
      if (res.data.kind === 'fund') u.role = 'vc';
      else if (res.data.kind === 'startup') u.role = 'startup';
      localStorage.setItem('user', JSON.stringify(u));
      toast.success(`Welcome to ${res.data.fund_name || preview.fund_name}!`);
      // Hard navigate so App.js re-reads localStorage and the new role/onboarding routes kick in
      window.location.href = res.data.kind === 'fund' ? '/vc/chat' : '/startup/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally { setAccepting(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-text-muted">Loading invitation...</div>;
  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-surface border border-border rounded-xl p-8 max-w-md text-center">
        <X size={36} className="text-danger mx-auto mb-4" />
        <h2 className="text-xl font-heading font-bold text-text-primary mb-2">Invitation not found</h2>
        <p className="text-sm text-text-secondary mb-5">{error}</p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">Go to Founderboard</button>
      </div>
    </div>
  );

  const isStartup = preview.kind === 'startup';
  const accent = isStartup ? 'bg-success' : 'bg-primary';
  const accentText = isStartup ? 'text-success' : 'text-primary';
  const accentBg = isStartup ? 'bg-success/5 border-success/30' : 'bg-primary/5 border-primary/30';

  const requiredEmail = preview.invited_email.toLowerCase();
  const userEmail = (storedUser?.email || '').toLowerCase();
  const isLoggedInAsCorrect = storedUser && userEmail === requiredEmail;
  const isLoggedInAsWrong = storedUser && userEmail !== requiredEmail;

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className={`${accentBg} border rounded-xl p-7 mb-5`} data-testid="invite-hero">
          <div className="text-xs uppercase tracking-wider text-text-muted mb-3">{isStartup ? 'Startup Team Invitation' : 'Fund Team Invitation'}</div>
          <h1 className="text-3xl font-heading font-black text-text-primary mb-2">
            <strong className={accentText}>{preview.invited_by_name || 'A teammate'}</strong> invited you to join
          </h1>
          <h2 className={`text-2xl font-heading font-bold ${accentText}`}>{preview.fund_name}</h2>
          <div className="mt-4 text-sm text-text-secondary">
            Role: <strong className="text-text-primary uppercase tracking-wider">{preview.role}</strong> · Invitation sent to <strong className="text-text-primary">{preview.invited_email}</strong>
          </div>
        </div>

        {!storedUser && (
          <div className="bg-surface border border-border rounded-xl p-7 text-center" data-testid="invite-needs-login">
            <Sparkle size={28} className="text-warning mx-auto mb-3" weight="fill" />
            {emailExists === null ? (
              <>
                <h3 className="font-heading font-bold text-lg text-text-primary mb-1">Setting up...</h3>
                <p className="text-sm text-text-secondary mb-5">Checking your account.</p>
              </>
            ) : emailExists ? (
              <>
                <h3 className="font-heading font-bold text-lg text-text-primary mb-1">Welcome back</h3>
                <p className="text-sm text-text-secondary mb-5">You already have a Founderboard account with <strong className="text-text-primary">{preview.invited_email}</strong>. Log in to accept this invitation.</p>
                <button onClick={() => navigate('/login', { state: { mode: 'login', prefilledEmail: preview.invited_email, returnTo: `/accept-team-invite/${token}` } })}
                  className={`w-full py-3 ${accent} text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2`} data-testid="invite-login-cta">
                  Log in & accept invitation <ArrowRight size={14} weight="bold" />
                </button>
              </>
            ) : (
              <>
                <h3 className="font-heading font-bold text-lg text-text-primary mb-1">One step to join</h3>
                <p className="text-sm text-text-secondary mb-5">Set a password for <strong className="text-text-primary">{preview.invited_email}</strong>. We'll add you straight to <strong className="text-text-primary">{preview.fund_name}</strong> — no extra setup.</p>
                <button onClick={() => navigate('/login', { state: { mode: 'signup', role: isStartup ? 'startup' : 'vc', prefilledEmail: preview.invited_email, returnTo: `/accept-team-invite/${token}` } })}
                  className={`w-full py-3 ${accent} text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2`} data-testid="invite-signup-cta">
                  Create account & accept <ArrowRight size={14} weight="bold" />
                </button>
                <button onClick={() => navigate('/login', { state: { mode: 'login', prefilledEmail: preview.invited_email, returnTo: `/accept-team-invite/${token}` } })}
                  className="mt-3 text-xs text-text-muted hover:text-text-primary underline" data-testid="invite-login-fallback">
                  Already have an account? Log in instead
                </button>
              </>
            )}
          </div>
        )}

        {isLoggedInAsWrong && (
          <div className="bg-surface border border-danger/30 rounded-xl p-7 text-center" data-testid="invite-wrong-account">
            <X size={28} className="text-danger mx-auto mb-3" weight="bold" />
            <h3 className="font-heading font-bold text-lg text-text-primary mb-1">You're logged in as a different account</h3>
            <p className="text-sm text-text-secondary mb-5">
              This invitation is for <strong className="text-text-primary">{preview.invited_email}</strong>. You're currently logged in as <strong className="text-text-primary">{storedUser.email}</strong>.
            </p>
            <button onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.reload(); }}
              className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold" data-testid="invite-logout-cta">
              Log out & switch
            </button>
          </div>
        )}

        {isLoggedInAsCorrect && (
          <div className="bg-surface border-2 border-success/30 rounded-xl p-7 text-center" data-testid="invite-ready">
            <CheckCircle size={28} className="text-success mx-auto mb-3" weight="fill" />
            <h3 className="font-heading font-bold text-lg text-text-primary mb-1">Ready to join {preview.fund_name}</h3>
            <p className="text-sm text-text-secondary mb-5">Logged in as <strong className="text-text-primary">{storedUser.email}</strong>.</p>
            <button onClick={accept} disabled={accepting} className={`w-full py-3 ${accent} text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2`} data-testid="invite-accept-btn">
              {accepting ? 'Joining...' : <>Accept invitation <ArrowRight size={14} weight="bold" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptTeamInvite;
